'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { Mail, ArrowRight, Shield, AlertCircle, Loader2, ShieldAlert, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const labels = {
  en: {
    internalPortal: 'Internal Staff Portal',
    subtitle: 'NautEX Operations & Administration',
    restrictedAccess: 'Restricted Access',
    restrictedNote: 'This portal is for authorized NautEX staff only. All access attempts are monitored and logged.',
    emailLabel: 'Staff Email Address',
    emailPlaceholder: 'staff@nautex.io',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    signIn: 'Sign In to Admin Portal',
    signingIn: 'Authenticating...',
    forgotPassword: 'Forgot password?',
    backToPlatform: '← Back to Platform User Login',
    invalidEmail: 'Enter a valid staff email',
    passwordRequired: 'Password is required',
    auditNotice: 'This login event will be recorded in the immutable NautEX audit log.',
    accessLevel: 'Access Level',
  },
  ar: {
    internalPortal: 'بوابة الموظفين الداخلية',
    subtitle: 'عمليات وإدارة NautEX',
    restrictedAccess: 'وصول مقيّد',
    restrictedNote: 'هذه البوابة مخصصة لموظفي NautEX المخولين فقط. يتم مراقبة وتسجيل جميع محاولات الوصول.',
    emailLabel: 'البريد الإلكتروني للموظف',
    emailPlaceholder: 'staff@nautex.io',
    passwordLabel: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    signIn: 'الدخول إلى بوابة المشرف',
    signingIn: 'جارٍ المصادقة...',
    forgotPassword: 'نسيت كلمة المرور؟',
    backToPlatform: '→ العودة إلى تسجيل دخول المستخدم',
    invalidEmail: 'أدخل بريدًا إلكترونيًا صحيحًا',
    passwordRequired: 'كلمة المرور مطلوبة',
    auditNotice: 'سيتم تسجيل هذا الدخول في سجل التدقيق الثابت لـ NautEX.',
    accessLevel: 'مستوى الوصول',
  },
};

type LoginForm = { email: string; password: string };

export default function AdminLoginContent() {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const t = labels[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const supabase = createClient();

  // Dev-only quick-fill (email + password from NEXT_PUBLIC_SEED_* env vars)
  const isDev = process.env.NODE_ENV !== 'production';
  const seedEmails = isDev ? {
    admin: process.env.NEXT_PUBLIC_SEED_ADMIN_EMAIL ?? 'seed.admin@nautex.tech',
    superadmin: process.env.NEXT_PUBLIC_SEED_SUPERADMIN_EMAIL ?? 'seed.superadmin@nautex.tech',
  } : null;
  const seedPasswords = isDev ? {
    admin: process.env.NEXT_PUBLIC_SEED_ADMIN_PASSWORD ?? '',
    superadmin: process.env.NEXT_PUBLIC_SEED_SUPERADMIN_PASSWORD ?? '',
  } : null;

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/verify-admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });

      const body = await res.json();

      if (!res.ok) {
        toast.error(body.error || 'Access denied.');
        return;
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
      });

      if (sessionError) {
        toast.error('Session could not be established. Please try again.');
        return;
      }

      const role = body.role as string;
      toast.success(`Signed in as ${role}`);
      window.location.href = '/admin-panel?tab=overview';
    } catch (err: any) {
      toast.error('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedAccounts = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      const res = await fetch('/api/admin/seed-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: 'NAUTEX_SEED_TRIGGER_2026' }),
      });
      const data = await res.json();

      if (res.status === 401) {
        setSeedResult(`❌ Auth rejected: ${data.detail ?? data.error ?? 'Unauthorized'}`);
        return;
      }

      if (res.ok || res.status === 207) {
        const accountResults = Object.entries(data.results || {}).filter(([k]) => k !== 'service_zones');
        const allOk = accountResults.every(([, v]) => (v as any).success);

        if (allOk) {
          setSeedResult('✅ All seed accounts ready! Fill credentials above and sign in.');
        } else {
          // Build a detailed failure message showing which account failed and why
          const failures = accountResults
            .filter(([, v]) => !(v as any).success)
            .map(([email, v]: [string, any]) => {
              const lastLog = (v.log as string[] | undefined)?.slice(-1)[0] ?? v.error ?? 'unknown error';
              return `${email.split('@')[0]}: ${lastLog}`;
            });
          setSeedResult(`⚠️ Partial seed — failed:\n${failures.join('\n')}`);
        }
      } else {
        setSeedResult(`❌ Error (${res.status}): ${data.detail ?? data.error ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      setSeedResult(`❌ Network error: ${err.message}`);
    } finally {
      setSeedLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir={dir} style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(3, 105, 161, 0.12) 0%, transparent 60%), var(--background)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <AppLogo size={28} />
          <div>
            <span className="font-bold text-sm text-foreground tracking-tight">NautEX</span>
            <span className="ml-2 text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full font-medium border border-red-500/20">
              {lang === 'ar' ? 'داخلي' : 'INTERNAL'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle lang={lang} onChange={setLang} />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Security alert banner */}
          <div className="mb-4 rounded-lg border border-red-500/25 bg-red-500/8 p-3 flex items-start gap-2.5">
            <ShieldAlert size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-red-400">{t.restrictedAccess}</p>
              <p className="text-xs text-red-300/70 mt-0.5">{t.restrictedNote}</p>
            </div>
          </div>

          <div className="card-surface shadow-2xl animate-slide-up">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <Shield size={20} className="text-red-400" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">{t.internalPortal}</h1>
                  <p className="text-xs text-muted-foreground">{t.subtitle}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    {t.emailLabel}
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      placeholder={t.emailPlaceholder}
                      className="input-base w-full pl-9 pr-3 py-2.5 text-sm"
                      {...register('email', {
                        required: t.invalidEmail,
                        pattern: { value: /^\S+@\S+\.\S+$/, message: t.invalidEmail },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
                      <AlertCircle size={12} />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-foreground">
                      {t.passwordLabel}
                    </label>
                    <Link
                      href="/forgot-password"
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      {t.forgotPassword}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t.passwordPlaceholder}
                      className="input-base w-full pl-9 pr-10 py-2.5 text-sm"
                      {...register('password', { required: t.passwordRequired })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
                      <AlertCircle size={12} />
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 text-sm flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin" />{t.signingIn}</>
                  ) : (
                    <>{t.signIn}<ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              {/* Dev-only quick-fill buttons — email + password */}
              {isDev && seedEmails && (
                <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-3">
                  <p className="text-xs font-semibold text-amber-400 mb-2">⚠ Dev Only — Quick Fill</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setValue('email', seedEmails.admin);
                        if (seedPasswords?.admin) setValue('password', seedPasswords.admin);
                      }}
                      className="text-xs px-2.5 py-1 rounded bg-purple-500/15 text-purple-400 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
                    >
                      Fill Admin
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setValue('email', seedEmails.superadmin);
                        if (seedPasswords?.superadmin) setValue('password', seedPasswords.superadmin);
                      }}
                      className="text-xs px-2.5 py-1 rounded bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors"
                    >
                      Fill Super Admin
                    </button>
                    <button
                      type="button"
                      onClick={handleSeedAccounts}
                      disabled={seedLoading}
                      className="text-xs px-2.5 py-1 rounded bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      {seedLoading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                      {seedLoading ? 'Seeding...' : '🌱 Seed Accounts'}
                    </button>
                  </div>
                  {seedResult && (
                    <p className="text-xs mt-2 text-amber-200/80 whitespace-pre-line">{seedResult}</p>
                  )}
                  <p className="text-xs text-amber-300/60 mt-1.5">⚠ Dev only — test accounts. Not shown in production.</p>
                </div>
              )}

              {/* Audit notice */}
              <p className="text-xs text-muted-foreground text-center">{t.auditNotice}</p>

              <Link
                href="/platform-user-login"
                className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t.backToPlatform}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}